import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Grid, Stack, Typography, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { ocrPalette } from "./ocrPalette";
import useDocumentOcrModel from "../../../hooks/useDocumentOcrModel";

/**
 * OcrUpload — click-to-upload / drag-and-drop screen for OCR documents.
 * Matches the neutral MUI 5 styling used by OcrResultView.
 *
 * Props:
 *  onSubmit?: (files: File[]) => void   // fired when the user runs OCR
 *  accept?: string                      // input accept list (default images + pdf)
 *  multiple?: boolean                   // allow multiple files (default true)
 *  maxSizeMB?: number                   // per-file size cap (default 10)
 *  busy?: boolean                       // shows a processing state on the action button
 *  title?: string
 *  hint?: string
 */
export default function OcrUpload({
  onSubmit,
  accept = "image/png,image/jpeg,image/webp,application/pdf",
  multiple = true,
  maxSizeMB = 10,
  busy = false,
  title = "Upload document",
  hint = "Upload a scan or photo of the document to run OCR.",
}) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const {ocrModel} = useDocumentOcrModel();

  // Build object URLs for image previews and clean them up when files change.
  const [previews, setPreviews] = useState({});
  useEffect(() => {
    const map = {};
    files.forEach((f) => {
      if (f.type.startsWith("image/")) map[f.name + f.size] = URL.createObjectURL(f);
    });
    setPreviews(map);
    return () => Object.values(map).forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const acceptList = accept.split(",").map((s) => s.trim()).filter(Boolean);

  const isAccepted = useCallback(
    (file) =>
      acceptList.length === 0 ||
      acceptList.some((rule) =>
        rule.endsWith("/*")
          ? file.type.startsWith(rule.slice(0, -1))
          : file.type === rule
      ),
    [acceptList]
  );

  const addFiles = useCallback(
    (incoming) => {
      const list = Array.from(incoming);
      if (list.length === 0) return;

      const accepted = [];
      let message = "";

      for (const file of list) {
        if (!isAccepted(file)) {
          message = `"${file.name}" is not a supported file type.`;
          continue;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
          message = `"${file.name}" exceeds the ${maxSizeMB} MB limit.`;
          continue;
        }
        accepted.push(file);
      }

      setError(message);
      if (accepted.length === 0) return;

      setFiles((prev) => {
        if (!multiple) return accepted.slice(0, 1);
        const seen = new Set(prev.map((f) => f.name + f.size));
        const merged = [...prev];
        accepted.forEach((f) => {
          if (!seen.has(f.name + f.size)) merged.push(f);
        });
        return merged;
      });
    },
    [isAccepted, maxSizeMB, multiple]
  );

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!dragActive) setDragActive(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }

  function handleSelect(e) {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  }

  function openPicker() {
    inputRef.current?.click();
  }

  function removeFile(index) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setError("");
  }

  function clearAll() {
    setFiles([]);
    setError("");
  }

  return (
    <Box
      sx={{
        bgcolor: ocrPalette.bg,
        borderRadius: "12px",
        p: 2,
        color: ocrPalette.text,
        fontSize: 13,
      }}
    >
      <Stack spacing={0.25} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: ocrPalette.text }}>
          {title}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: 12, color: ocrPalette.muted }}>{hint}</Typography>
        )}
      </Stack>

      {/* ---- Dropzone ---- */}
      <Box
        role="button"
        tabIndex={0}
        aria-label="Upload file: click or drag and drop"
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 1,
          px: 2,
          py: { xs: 4, sm: 6 },
          borderRadius: "10px",
          border: `2px dashed ${dragActive ? ocrPalette.accent : ocrPalette.border}`,
          bgcolor: dragActive ? ocrPalette.accentBg : ocrPalette.surface,
          cursor: "pointer",
          transition: "background-color 0.15s ease, border-color 0.15s ease",
          outline: "none",
          "&:hover": { borderColor: ocrPalette.accent, bgcolor: ocrPalette.accentBg },
          "&:focus-visible": { borderColor: ocrPalette.accent },
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: ocrPalette.accentBg,
            color: ocrPalette.accent,
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 26 }} />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: ocrPalette.text }}>
          <Box component="span" sx={{ color: ocrPalette.accent }}>
            Click to upload
          </Box>{" "}
          or drag and drop
        </Typography>
        <Typography sx={{ fontSize: 12, color: ocrPalette.muted }}>
          {formatAccept(acceptList)} · up to {maxSizeMB} MB
          {multiple ? " · multiple files allowed" : ""}
        </Typography>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleSelect}
          style={{ display: "none" }}
        />
      </Box>

      {/* ---- Error bar ---- */}
      {error && (
        <Box
          sx={{
            mt: 1.5,
            bgcolor: ocrPalette.failBg,
            color: ocrPalette.fail,
            borderRadius: "8px",
            px: 1.5,
            py: 1,
            fontSize: 12,
          }}
        >
          {error}
        </Box>
      )}

      {/* ---- Selected files ---- */}
      {files.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {files.map((file, i) => {
            const isImage = file.type.startsWith("image/");
            const previewUrl = previews[file.name + file.size];
            return (
              <Stack
                key={file.name + file.size + i}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  bgcolor: ocrPalette.surface,
                  border: `1px solid ${ocrPalette.border}`,
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: "6px",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: ocrPalette.grayBg,
                    color: ocrPalette.muted,
                  }}
                >
                  {isImage && previewUrl ? (
                    <Box
                      component="img"
                      src={previewUrl}
                      alt={file.name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : isImage ? (
                    <ImageOutlinedIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />
                  )}
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: ocrPalette.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: ocrPalette.muted }}>
                    {formatSize(file.size)}
                  </Typography>
                </Box>

                <Button
                  onClick={() => removeFile(i)}
                  aria-label={`Remove ${file.name}`}
                  sx={{
                    minWidth: 0,
                    p: 0.5,
                    color: ocrPalette.muted,
                    borderRadius: "6px",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.04)", color: ocrPalette.fail },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </Button>
              </Stack>
            );
          })}
        </Stack>
      )}

      {/* ---- Footer actions ---- */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mt: 1.5 }}
      >
        <Typography sx={{ fontSize: 11, color: ocrPalette.muted }}>
          {files.length > 0
            ? `${files.length} file${files.length > 1 ? "s" : ""} ready`
            : "No file selected"}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
          {(files.length > 0 || ocrModel.trim() !== "") ? (
            <UploadButton onClick={clearAll} disabled={busy}>
              Clear
            </UploadButton>
          ) : <></>}
          <UploadButton
            primary
            disabled={files.length === 0 || ocrModel.trim() === "" || busy}
            onClick={() => onSubmit?.(files, ocrModel)}
          >
            {busy ? "Processing…" : "Run OCR"}
          </UploadButton>
        </Stack>
      </Stack>
    </Box>
  );
}

function UploadButton({ children, onClick, primary, disabled }) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      disableElevation
      sx={{
        textTransform: "none",
        fontSize: 12,
        px: 1.5,
        py: 0.625,
        minWidth: 0,
        borderRadius: "7px",
        border: primary
          ? `1px solid ${ocrPalette.accentBorder}`
          : `1px solid ${ocrPalette.border}`,
        bgcolor: primary ? ocrPalette.accentBg : ocrPalette.surface,
        color: primary ? ocrPalette.accent : ocrPalette.text,
        "&:hover": { bgcolor: primary ? ocrPalette.accentHover : ocrPalette.hover },
        "&.Mui-disabled": {
          opacity: 0.5,
          bgcolor: primary ? ocrPalette.accentBg : ocrPalette.surface,
          color: primary ? ocrPalette.accent : ocrPalette.text,
        },
      }}
    >
      {children}
    </Button>
  );
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatAccept(acceptList) {
  if (acceptList.length === 0) return "Any file";
  const exts = acceptList.map((rule) => {
    if (rule === "application/pdf") return "PDF";
    if (rule.startsWith("image/")) return rule.replace("image/", "").toUpperCase();
    return rule;
  });
  return Array.from(new Set(exts)).join(", ");
}
